// Raw-log replay approvals (APS-252): staff approve one apsio-replay run for an exact window
// (UTC) and scope, with a reason. The answer carries a token shown here once, with the command
// to run; it is good once within 30 minutes and only for that selection, and apsio-replay's audit
// rows name the staff member who approved it. Leaving the panel forgets it. Console D (APS-412,
// admin-d.md E): the form is a window opened in its section, the window as two UTC fields
// (`AAAA-MM-DD HH:MM`, never the browser's own date input), the token whole with its own copy,
// the command in a code window across the section (it scrolls, never wraps: APS-379).
import { CodeTabs, copyText, ErrorState as ErrorLine, Icon, Section, useToast } from '@apsio/ui'
import { type FormEvent, useId, useState } from 'react'
import { approveReplay, type ReplayApproval, useAdminMutation } from '../api/queries'
import {
  FormWindow,
  parseUtc,
  ReasonField,
  UtcRange,
  type UtcRangeValue,
  utcRangeError,
} from '../components/kit'
import { ErrorDetail, errorMessage } from '../components/states'
import { type MessageId, useI18n } from '../i18n'

export const SCOPES = ['all', 'org', 'project', 'app'] as const
type Scope = (typeof SCOPES)[number]
const UUID = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

/** A typed `AAAA-MM-DD HH:MM` (UTC) as RFC 3339 in whole seconds; null when it is not one. */
export function utc(value: string): string | null {
  const iso = parseUtc(value)
  return iso ? iso.replace('.000Z', 'Z') : null
}

/** What to run with the token: the token goes in the environment, never in the arguments. */
export function command(a: ReplayApproval): string {
  const at = (s: number) => new Date(s * 1000).toISOString().replace('.000Z', 'Z')
  const scope = a.selection.org
    ? ` --org ${a.selection.org}`
    : a.selection.project
      ? ` --project ${a.selection.project}`
      : a.selection.app
        ? ` --app ${a.selection.app}`
        : ''
  return `APSIO_REPLAY_APPROVAL=<token> apsio-replay --from ${at(a.selection.from)} --to ${at(a.selection.to)}${scope}${a.selection.dead ? ' --dead' : ''} --execute`
}

function ApprovalForm({
  onApproved,
  onCancel,
}: {
  onApproved: (a: ReplayApproval) => void
  onCancel: () => void
}) {
  const { t } = useI18n()
  const ids = { scope: useId(), id: useId(), dead: useId() }
  const [range, setRange] = useState<UtcRangeValue>({ from: '', to: '' })
  const [scope, setScope] = useState<Scope>('all')
  const [id, setId] = useState('')
  const [dead, setDead] = useState(false)
  const [reason, setReason] = useState('')
  const approve = useAdminMutation(approveReplay, [])

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const wrong = utcRangeError(range)
    const from = utc(range.from)
    const to = utc(range.to)
    if (wrong || !from || !to) {
      // The fields say what is wrong under themselves, once left: leave the one still in focus
      // (a submit by Enter), so its message shows there and not a second time under the form.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
      return
    }
    approve.mutate(
      {
        from,
        to,
        org: scope === 'org' ? id.trim() : null,
        project: scope === 'project' ? id.trim() : null,
        app: scope === 'app' ? id.trim() : null,
        dead,
        reason: reason.trim(),
      },
      { onSuccess: onApproved },
    )
  }

  return (
    <FormWindow
      title={t('system.replay.formTitle')}
      submitLabel={t('system.replay.approve')}
      busy={approve.isPending}
      onSubmit={submit}
      onCancel={onCancel}
      alert={
        approve.isError ? (
          <ErrorLine
            message={errorMessage(t, approve.error)}
            detail={<ErrorDetail error={approve.error} />}
          />
        ) : null
      }
    >
      <UtcRange value={range} onChange={setRange} required />
      <p className="wide hint-d">{t('system.replay.utc')}</p>
      <div className="field-d">
        <label className="lbl" htmlFor={ids.scope}>
          {t('system.replay.scope')}
        </label>
        <select
          id={ids.scope}
          className="select"
          value={scope}
          onChange={(e) => setScope(e.target.value as Scope)}
        >
          {SCOPES.map((s) => (
            <option key={s} value={s}>
              {t(`system.replay.scope.${s}` as MessageId)}
            </option>
          ))}
        </select>
      </div>
      {scope !== 'all' ? (
        <div className="field-d">
          <label className="lbl" htmlFor={ids.id}>
            {t('system.replay.id')}
          </label>
          <input
            id={ids.id}
            name="id"
            className="input mono"
            required
            pattern={UUID.source.slice(1, -1)}
            autoComplete="off"
            spellCheck={false}
            translate="no"
            value={id}
            onChange={(e) => setId(e.target.value)}
          />
        </div>
      ) : null}
      <label className="wide check-d" htmlFor={ids.dead}>
        <input
          id={ids.dead}
          name="dead"
          type="checkbox"
          checked={dead}
          onChange={(e) => setDead(e.target.checked)}
        />
        <span>{t('system.replay.dead')}</span>
      </label>
      <ReasonField value={reason} onChange={setReason} />
    </FormWindow>
  )
}

/**
 * The answer, once: the token whole (it wraps, never clipped) with its own Copiar, and the
 * command, then Concluir forgets both.
 */
function Approved({ approval, onDone }: { approval: ReplayApproval; onDone: () => void }) {
  const { t, dateTime } = useI18n()
  const { toast, show } = useToast()
  const tokenId = useId()
  return (
    <div className="replay-done" role="status">
      <div className="field-d">
        <label className="lbl" htmlFor={tokenId}>
          {t('system.replay.token')}
        </label>
        <div className="replay-token">
          <textarea
            id={tokenId}
            className="input mono"
            readOnly
            rows={2}
            value={approval.token}
            onFocus={(e) => e.currentTarget.select()}
            spellCheck={false}
            translate="no"
          />
          <button
            type="button"
            className="btn sm"
            aria-label={t('system.replay.copyToken')}
            onClick={async () =>
              show((await copyText(approval.token)) ? t('d.code.copied') : t('d.code.copyFailed'))
            }
          >
            <Icon name="copy" small />
            {t('d.code.copy')}
          </button>
        </div>
        {toast}
        <span className="hint">
          {t('system.replay.tokenHelp', { at: dateTime(approval.expires_at) })}
        </span>
      </div>
      <CodeTabs
        label={t('system.replay.command')}
        tabs={[{ id: 'shell', label: 'shell', code: command(approval), copy: command(approval) }]}
      />
      <div className="acts">
        <button type="button" className="btn" onClick={onDone}>
          {t('system.replay.done')}
        </button>
      </div>
    </div>
  )
}

export function ReplayApprovalPanel() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const [approval, setApproval] = useState<ReplayApproval | null>(null)
  return (
    <Section
      title={t('system.replay.title')}
      meta={t('system.replay.meta')}
      right={
        !open && !approval ? (
          <button type="button" className="btn sm" onClick={() => setOpen(true)}>
            {t('system.replay.open')}
          </button>
        ) : undefined
      }
    >
      <p className="ops-def">{t('system.replay.sub')}</p>
      {approval ? (
        <Approved approval={approval} onDone={() => setApproval(null)} />
      ) : open ? (
        <div className="ops-win">
          <ApprovalForm
            onApproved={(a) => {
              setOpen(false)
              setApproval(a)
            }}
            onCancel={() => setOpen(false)}
          />
        </div>
      ) : null}
    </Section>
  )
}
